/**
 * Single canonical hook for reading the CMS-landing document.
 *
 * Why this exists: several pages (Landing, Login, Register, Legal, Support,
 * customer portal) all render brand colours + tagline pulled from the same
 * `/cms/landing` endpoint. Historically each page used a different React Query
 * key, so no cross-page dedupe — every navigation flashed the default (orange)
 * theme for a beat before the fetched CMS theme (e.g. teal) took over.
 *
 * This hook:
 *   1. Hydrates the initial state SYNCHRONOUSLY from the shared localStorage
 *      cache, so the first paint after a page load already renders in the right
 *      brand colours (no FOUC).
 *   2. Uses one canonical React Query key so all pages share a single fetch.
 *   3. Writes the response back to localStorage on every successful fetch,
 *      keeping the cache warm for the *next* page visit / cold reload.
 *
 * The exported ``T`` object is guaranteed non-null and always contains a
 * fully-populated customer_theme colour palette (defaults merged in) — pages can
 * consume it directly without null-checking.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";

export const CMS_CACHE_KEY = "fuelexpress_theme_cache_v1";

const DEFAULT_THEME = {
  gradient_from: "#EA580C", gradient_to: "#F59E0B", accent: "#F97316",
  card_bg: "#FFFFFF", panel_bg: "#FFF9F5", text_on_gradient: "#FFFFFF",
};

const DEFAULT_STAFF_THEME = {
  gradient_from: "#0F172A", gradient_to: "#1E293B", accent: "#38BDF8",
  card_bg: "#FFFFFF", panel_bg: "#F1F5F9", text_on_gradient: "#F8FAFC",
};

const DEFAULT_BRAND = {
  name: "FUELEXPRESS",
  logo_url: "",
  customer_login_tagline: "Doorstep diesel delivery",
  customer_login_enabled: true,
};

function readCache() {
  try { return JSON.parse(localStorage.getItem(CMS_CACHE_KEY) || "null") || {}; }
  catch { return {}; }
}

function writeCache(content) {
  try {
    localStorage.setItem(CMS_CACHE_KEY, JSON.stringify({
      customer_theme: content?.customer_theme || null,
      staff_theme: content?.staff_theme || null,
      brand: content?.brand || null,
      _cached_at: Date.now(),
    }));
  } catch { /* private mode / quota exceeded — degrade silently */ }
}

/**
 * @param {object} opts
 * @param {"customer"|"staff"} [opts.panel="customer"] which theme sub-object to
 *   resolve. Doesn't affect what's fetched — only which colour palette is
 *   returned in `T`.
 */
export function useLandingCMS({ panel = "customer" } = {}) {
  const cached = readCache();

  // Shared query key — same as ThemeInjector and Landing.jsx. We deliberately
  // do NOT pass initialData here: Landing.jsx registers its own initialData
  // that hydrates the FULL CMS content from localStorage. If we set initialData
  // here with only theme+brand, it would clobber Landing's full-content cache
  // and blank the hero/features/etc. FOUC is already handled by the sync script
  // in index.html.
  const { data } = useQuery({
    queryKey: ["cms-landing"],
    queryFn: () => api.get("/cms/landing").then((r) => r.data),
    staleTime: 60_000,
  });

  // React Query v5 removed useQuery.onSuccess — write cache via effect so
  // subsequent visits/reloads hit the synchronous FOUC hydrator in index.html.
  useEffect(() => {
    if (data) writeCache(data?.content || data);
  }, [data]);

  const content = data?.content || data || {};
  const brand = { ...DEFAULT_BRAND, ...(cached.brand || {}), ...(content.brand || {}) };

  const themeKey = panel === "staff" ? "staff_theme" : "customer_theme";
  const defaults = panel === "staff" ? DEFAULT_STAFF_THEME : DEFAULT_THEME;
  const T = {
    ...defaults,
    ...(cached[themeKey] || {}),
    ...(content[themeKey] || {}),
  };

  return { T, brand, content };
}
